/**
 * L'affectation du résultat d'une EURL ou d'une SASU : ce qui va à la réserve
 * légale, ce qui peut se distribuer, ce qui reste en report à nouveau.
 *
 * LA RÉSERVE LÉGALE N'EST PAS UNE OPTION. Dans une SARL comme dans une société
 * par actions, un vingtième au moins du bénéfice, diminué des pertes
 * antérieures, va à la réserve légale jusqu'à ce qu'elle atteigne le dixième
 * du capital (code de commerce, article L. 232-10). Avec 1 000 € de capital,
 * elle est pleine dès 100 € : la plupart des solopreneurs la remplissent la
 * première année bénéficiaire, puis n'y pensent plus. Le document la calcule,
 * parce qu'une décision qui l'oublie est nulle sur ce point.
 *
 * LE BÉNÉFICE DISTRIBUABLE (article L. 232-11) est le bénéfice de l'exercice,
 * diminué des pertes antérieures et de la dotation à la réserve, augmenté du
 * report à nouveau bénéficiaire. Au-delà, un dividende serait fictif. Les
 * réserves facultatives, qu'un associé peut aussi décider de distribuer, ne
 * sont pas prises en compte : la décision le dirait autrement, et le cas est
 * rare chez un solopreneur.
 *
 * Tous les montants sont en euros et arrondis au centime ; la dotation est
 * arrondie au centime SUPÉRIEUR, pour ne jamais tomber sous le vingtième.
 */

export const TAUX_RESERVE_LEGALE = 0.05;
export const PLAFOND_RESERVE_LEGALE = 0.1;

const centimes = (v) => Math.round(v * 100) / 100;
const positif = (v) => Math.max(0, v);

/**
 * @param resultat        résultat de l'exercice : positif pour un bénéfice,
 *                        négatif pour une perte
 * @param capital         capital social
 * @param reserveLegale   réserve légale déjà constituée
 * @param reportANouveau  report à nouveau avant affectation, négatif s'il
 *                        porte des pertes antérieures
 * @param dividendes      dividendes que l'associé veut se verser
 */
export function affecterResultat({ resultat, capital, reserveLegale = 0, reportANouveau = 0, dividendes = 0 }) {
    const perte = resultat < 0;
    // Le bénéfice diminué des pertes antérieures, base de la réserve légale.
    const base = centimes(resultat + Math.min(0, reportANouveau));

    const manqueReserve = positif(centimes(PLAFOND_RESERVE_LEGALE * capital - reserveLegale));
    const dotation =
        !perte && base > 0
            ? Math.min(Math.ceil(TAUX_RESERVE_LEGALE * base * 100) / 100, manqueReserve)
            : 0;

    // Une année de perte, rien ne se distribue ici : le résultat va en report
    // à nouveau, et l'écran le dit plutôt que d'aller chercher des réserves.
    const distribuable = perte ? 0 : positif(centimes(base - dotation + Math.max(0, reportANouveau)));
    const demande = positif(centimes(dividendes));
    const verses = Math.min(demande, distribuable);

    return {
        perte,
        base,
        dotation,
        reserveApres: centimes(reserveLegale + dotation),
        reservePleine: centimes(reserveLegale + dotation) >= centimes(PLAFOND_RESERVE_LEGALE * capital),
        distribuable,
        dividendes: verses,
        // Un dividende demandé au-delà du distribuable est ramené à celui-ci,
        // et l'écran prévient : c'est un dividende fictif qu'on évite.
        ramene: demande > distribuable,
        reportApres: centimes(reportANouveau + resultat - dotation - verses),
    };
}
